import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, MapPin, Heart } from 'lucide-react';
import { BusinessListItem } from '../types';

interface BusinessCardProps {
  business: BusinessListItem;
  index?: number;
}

const BusinessCard: React.FC<BusinessCardProps> = ({ business, index = 0 }) => {
  const isOpen = () => {
    if (!business.businessOperatingHours) return null;
    
    const now = new Date();
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const today = days[now.getDay()];
    
    const todayHours = business.businessOperatingHours.find(h => h.day === today);
    if (!todayHours || !todayHours.isOpen) return false;
    
    const currentTime = now.getHours() * 100 + now.getMinutes();
    const openTime = parseInt(todayHours.open.replace(':', ''));
    const closeTime = parseInt(todayHours.close.replace(':', ''));
    
    return currentTime >= openTime && currentTime <= closeTime;
  };

  const openStatus = isOpen();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.4 }}
    >
      <Link 
        to={`/shop/${business.businessId}`}
        className="group block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100"
      >
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden">
          {business.businessImage ? (
            <img
              src={business.businessImage}
              alt={business.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary-light to-primary/20 flex items-center justify-center">
              <span className="text-6xl font-display font-bold text-primary/30">
                {business.name.charAt(0)}
              </span>
            </div>
          )}
          
          {/* Favorite Button */}
          <button 
            className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
            onClick={(e) => {
              e.preventDefault();
              // Handle favorite
            }}
          >
            <Heart size={18} className="text-gray-600" />
          </button>

          {/* Open/Closed Badge */}
          {openStatus !== null && (
            <div className={`absolute bottom-3 left-3 px-3 py-1 rounded-full text-xs font-medium ${
              openStatus 
                ? 'bg-green-500 text-white' 
                : 'bg-gray-900/80 text-white'
            }`}>
              {openStatus ? 'Open Now' : 'Closed'}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-semibold text-lg text-prim-text group-hover:text-primary transition-colors line-clamp-1">
              {business.name}
            </h3>
            
            {/* Rating */}
            <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
              <Star size={16} className="text-amber-400 fill-amber-400" />
              <span className="text-sm font-medium text-prim-text">
                {business.rating?.toFixed(1) || '4.5'}
              </span>
            </div>
          </div>

          {/* Location */}
          <div className="flex items-center text-gray-500 text-sm mb-3">
            <MapPin size={14} className="mr-1 flex-shrink-0" />
            <span className="line-clamp-1">{business.address}, {business.postCode}</span>
          </div>

          {/* Distance & Reviews */}
          <div className="flex items-center justify-between text-sm">
            {business.distance !== undefined && (
              <span className="text-gray-500">
                {business.distance < 1 
                  ? `${Math.round(business.distance * 1000)}m away`
                  : `${business.distance.toFixed(1)}km away`
                }
              </span>
            )}
            
            <span className="text-gray-400">
              {business.reviewCount || 0} reviews
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default BusinessCard;

